import { FC, useMemo, useRef } from 'react';
import { Typography } from 'antd';
import { isArray, isNotEmptyAny } from '@lhb/func';
import cs from 'classnames';
import IconFont from '@/common/components/IconFont';
import SiteMapExplainExtra, { ParagraphProps } from '@/common/components/business/SiteMap/SiteMapExplainExtra';
import V2Predict from '@/common/components/business/AreaSalesPredict/V2Predict';
import { markTypeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import { fullEditorCss } from '@/common/components/config-v2';
const { Text } = Typography;

interface ScoreSummaryProps {
  detail: any,
  overviewData: any;
  predictSale: boolean;
  configSwitch: any;
  shareParams?:Object;// 分享页数据
  isSemirModel?: boolean; // 是否是森马模型
}

/**
 * @Description 评分概要(包含预测营业额) 四维评分时使用
 */
const ScoreSummaryHorizontal: FC<ScoreSummaryProps> = ({
  detail,
  overviewData,
  predictSale,
  configSwitch,
  shareParams = {},
  isSemirModel = false,
}) => {
  const evaluateInfoStr = useMemo(() => {
    const { marketScore, firstLevelName } = overviewData || {};
    if (marketScore > 70) {
      return `有利于${firstLevelName}经营`;
    }
    if (marketScore > 50 && marketScore <= 70) {
      return `经营${firstLevelName}一般`;
    }
    if (marketScore <= 50) {
      return `不利于${firstLevelName}经营`;
    }
    return '-';
  }, [overviewData]);

  // 四维评分时弹框中展示的评分文案
  const extraParagraph = useMemo(() => {
    const { markType, scores } = overviewData || {};
    if (markTypeEnum.four !== markType) return null;
    if (!isArray(scores) || !scores.length) return null;

    const filtered = scores.filter(itm => !!itm.name && !!itm.msg);
    if (!filtered.length) return null;

    const parag: ParagraphProps = {
      title: '评分说明',
      descriptions: filtered.map(itm => `${itm.name}：${itm.msg}`)
    };
    return parag;
  }, [overviewData]);

  const shadowRef: any = useRef();

  return (
    <div className={styles.scoreSummaryHorizontal}>
      <div className={styles.firstBlock}>
        <div className={styles.scoreTitle}>评分</div>
        <div className={styles.totalScoreInfo}>
          <div className={cs(styles.totalScoreVal, 'mr-4')}>{overviewData?.marketScore}</div>
          {isSemirModel || detail?.showScoreDescriptionFlag === 3 ? <></>
            : <div>
              <IconFont
                iconHref='iconxq_ic_shuoming_normal'
                className='mr-2 c-666 fs-12'
                tooltipConfig={{
                  placement: 'bottom',
                  overlayInnerStyle: {
                    width: '380px',
                    padding: '16px',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    ...(extraParagraph ? {
                      maxHeight: 'calc(50vh)',
                      overflowY: 'auto',
                    } : {})
                  },
                  color: '#fff',
                  onOpenChange(open) {
                    // left: 737px; top: 137px; transform-origin: 50% -4px;
                    if (detail?.showScoreDescriptionFlag === 2 && detail?.scoreDescription && open) {
                      setTimeout(() => {
                        // 创建Shadow DOM根节点
                        const shadowRoot = shadowRef.current.attachShadow({ mode: 'open' });
                        const contentDiv = document.createElement('div');
                        contentDiv.innerHTML = detail?.scoreDescription;
                        // 创建样式
                        const style = document.createElement('style');
                        style.textContent = fullEditorCss;
                        // 添加到Shadow DOM
                        shadowRoot.appendChild(style);
                        shadowRoot.appendChild(contentDiv);
                      }, 1);
                    }
                  },
                  title: detail?.showScoreDescriptionFlag === 2 ? (
                    <div ref={shadowRef}></div>
                  ) : (<SiteMapExplainExtra
                    markType={overviewData?.markType}
                    isPredictSale={predictSale}
                    shareParams={shareParams}
                    extraParagraphs={extraParagraph ? [extraParagraph] : []}
                  />),
                }}
              />
            </div>
          }
          { configSwitch?.showIndustryBenefitContent
            ? <div className='c-222 fs-12'>
              <Text
                style={predictSale || isNotEmptyAny(overviewData.rankingText) ? { maxWidth: '150px' } : { maxWidth: '250px' }}
                ellipsis={{ tooltip: evaluateInfoStr }}
                className='c-222 fs-12'
              >
                <span>
                    ({evaluateInfoStr})
                </span>
              </Text>
            </div>
            : <></> }
        </div>
      </div>
      {/* 预测营业额 */}
      {
        predictSale ? <div className={styles.forecastSection}>
          <V2Predict
            data={overviewData}
            style={{
              height: '50px',
              backgroundImage: 'none',
              backgroundColor: '#ffffffb3'
            }}
          />
          {
            isNotEmptyAny(overviewData.rankingText) ? <div className={cs(styles.comprehensiveRankCon, 'ml-8')}>
              <div className='c-222'>
                <span>市内前</span>
                <span className={styles.rankText}>{overviewData.rankingText}</span>
              </div>
              <div className='c-999 mt-2'>
                综合评分
              </div>
            </div> : <></>
          }
        </div> : <>
          { isNotEmptyAny(overviewData.rankingText) ? <div className={styles.comprehensiveRankConIndividual}>
            <div className='c-999'>综合评分</div>
            <div className='ml-4'>
              <span className='c-222'>市内前</span>
              <span className={cs('c-222', styles.rankText)}>{overviewData.rankingText}</span>
            </div>
          </div> : <></>}
        </>
      }
      <div className={styles.rightIcon}>
        <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_score.png`} width='100%' height='100%'/>
      </div>
    </div>
  );
};

export default ScoreSummaryHorizontal;
